import { Injectable } from '@angular/core';
import { ApiServiceBase } from '@api/abstracts';
import { Subscription } from '@api/models/dashboard';
import { environment } from '@environments/environment';
import { from, map, Observable } from 'rxjs';

/**
 * Per-vendor subscription mutations (physical store).
 * Admin API (base already includes /api/v1):
 * - POST  /stores/{storeId}/vendors/{userId}/subscription             (activate)
 * - PATCH /stores/{storeId}/vendors/{userId}/subscription/cancel      (schedule cancellation)
 * - PATCH /stores/{storeId}/vendors/{userId}/subscription/reactivate  (undo a scheduled cancellation)
 *
 * Mutations return a Subscription DTO; callers re-fetch the vendor list afterwards rather than
 * mapping it back (the list DTO carries a different, richer shape).
 */
@Injectable({
  providedIn: 'root',
})
export class SubscriptionsData extends ApiServiceBase<Subscription> {
  constructor() {
    super('stores', 'evoqAdmin');
  }

  activate(storeId: string, userId: string, subscriptionPlanId?: string): Observable<Subscription> {
    if (environment.isMockEnabled) {
      return from(import('@mock/schema/dashboard/physical-team.mock')).pipe(
        map((mock) => mock.activateSubscriptionMock(userId))
      );
    }

    return this.customRequest<Subscription>({
      method: 'POST',
      url: `${storeId}/vendors/${userId}/subscription`,
      body: subscriptionPlanId ? { subscriptionPlanId } : {},
    });
  }

  cancel(storeId: string, userId: string): Observable<Subscription> {
    if (environment.isMockEnabled) {
      return from(import('@mock/schema/dashboard/physical-team.mock')).pipe(
        map((mock) => mock.cancelSubscriptionMock(userId))
      );
    }

    return this.customRequest<Subscription>({
      method: 'PATCH',
      url: `${storeId}/vendors/${userId}/subscription/cancel`,
    });
  }

  reactivate(storeId: string, userId: string): Observable<Subscription> {
    if (environment.isMockEnabled) {
      return from(import('@mock/schema/dashboard/physical-team.mock')).pipe(
        map((mock) => mock.reactivateSubscriptionMock(userId))
      );
    }

    return this.customRequest<Subscription>({
      method: 'PATCH',
      url: `${storeId}/vendors/${userId}/subscription/reactivate`,
    });
  }
}
